import { BiographyVariables } from './biographyParser';

export interface ZenAfPersonalizationContext {
  specificFrustrations: string[];
  workStressors: string[];
  relationshipChallenges: string[];
  personalStrengths: string[];
  copingMechanisms: string[];
  environmentalAnchors: string[];
  victoryReferences: string[];
  rawLanguageIntensity: 'moderate' | 'high' | 'maximum';
}

export const createZenAfPersonalizationContext = (
  variables: BiographyVariables, 
  questionnaire: any
): ZenAfPersonalizationContext => {
  return {
    specificFrustrations: extractSpecificFrustrations(variables, questionnaire),
    workStressors: extractWorkStressors(variables),
    relationshipChallenges: extractRelationshipChallenges(variables),
    personalStrengths: extractPersonalStrengths(variables),
    copingMechanisms: variables.coping_mechanisms || [],
    environmentalAnchors: extractEnvironmentalAnchors(variables),
    victoryReferences: variables.victories || [],
    rawLanguageIntensity: determineLanguageIntensity(questionnaire)
  };
};

const extractSpecificFrustrations = (variables: BiographyVariables, questionnaire: any): string[] => {
  const frustrations: string[] = [];
  
  // From questionnaire responses - CRITICAL for targeting
  if (questionnaire?.specificFocus) {
    frustrations.push(questionnaire.specificFocus);
  }
  if (questionnaire?.triggers) {
    frustrations.push(questionnaire.triggers);
  }
  if (questionnaire?.groupFrustration) {
    frustrations.push(questionnaire.groupFrustration);
  }
  
  // From biography data - MINE for specific details
  if (variables.stressors?.personal?.length > 0) {
    frustrations.push(...variables.stressors.personal);
  }
  if (variables.stressors?.work_related?.length > 0) {
    frustrations.push(...variables.stressors.work_related);
  }
  
  // Extract difficult people and situations for targeting
  if (variables.relationships?.conflicts?.length > 0) {
    frustrations.push(...variables.relationships.conflicts.map(conflict => `dealing with ${conflict}'s bullshit`));
  }
  
  // Extract relationship drama for personalized roasts
  if (variables.relationships?.family_dynamics?.length > 0) {
    frustrations.push(...variables.relationships.family_dynamics.map(dynamic => `family drama: ${dynamic}`));
  }
  
  // Extract work-specific annoyances - use available colleagues data
  if (variables.work?.colleagues?.length > 0) {
    frustrations.push(...variables.work.colleagues.map(colleague => `${colleague}'s workplace nonsense`));
  }
  
  return frustrations;
};

const extractWorkStressors = (variables: BiographyVariables): string[] => {
  const stressors: string[] = [];
  
  if (variables.work?.stress_sources?.length > 0) {
    stressors.push(...variables.work.stress_sources);
  }
  
  // Add context about workplace dynamics
  if (variables.names?.colleagues?.length > 0) {
    stressors.push(`workplace dynamics with ${variables.names.colleagues[0]}`);
  }
  
  if (variables.work?.company?.length > 0) {
    stressors.push(`organizational stress at ${variables.work.company[0]}`);
  }
  
  return stressors;
};

const extractRelationshipChallenges = (variables: BiographyVariables): string[] => {
  const challenges: string[] = [];
  
  if (variables.relationships?.conflicts?.length > 0) {
    challenges.push(...variables.relationships.conflicts);
  }
  
  if (variables.relationships?.romantic?.length > 0) {
    challenges.push(...variables.relationships.romantic);
  }
  
  if (variables.relationships?.family_dynamics?.length > 0) {
    challenges.push(...variables.relationships.family_dynamics);
  }
  
  return challenges;
};

const extractPersonalStrengths = (variables: BiographyVariables): string[] => {
  const strengths: string[] = [];
  
  if (variables.values?.length > 0) {
    strengths.push(...variables.values);
  }
  
  if (variables.victories?.length > 0) {
    strengths.push(...variables.victories);
  }
  
  if (variables.hobbies?.length > 0) {
    strengths.push(...variables.hobbies.map(hobby => `finding peace through ${hobby}`));
  }
  
  return strengths;
};

const extractEnvironmentalAnchors = (variables: BiographyVariables): string[] => {
  const anchors: string[] = [];
  
  if (variables.locations?.favorite_places?.length > 0) {
    anchors.push(...variables.locations.favorite_places);
  }
  
  if (variables.locations?.home?.length > 0) {
    anchors.push(...variables.locations.home.map(home => `the safety of ${home}`));
  }
  
  if (variables.names?.pets?.length > 0) {
    anchors.push(...variables.names.pets.map(pet => `${pet}'s calming presence`));
  }
  
  return anchors;
};

const determineLanguageIntensity = (questionnaire: any): 'moderate' | 'high' | 'maximum' => {
  if (questionnaire?.preferredStyle?.includes('Raw and unfiltered')) {
    return 'maximum';
  }
  if (questionnaire?.preferredStyle?.includes('Blunt truth')) {
    return 'high';
  }
  return 'moderate';
};

export const generateOriginalZenAfPrompt = (
  basePrompt: string,
  personalizationContext: ZenAfPersonalizationContext,
  variables: BiographyVariables
): string => {
  let enhancedPrompt = basePrompt;
  
  // DYNAMIC BIOGRAPHICAL COMEDY GENERATION - Replace static templates
  enhancedPrompt += `\n\nDYNAMIC PERSONALIZATION ENGINE - NO TEMPLATES ALLOWED:`;
  
  if (personalizationContext.specificFrustrations.length > 0) {
    enhancedPrompt += `\n\nSPECIFIC FRUSTRATIONS FOR SITUATIONAL COMEDY CREATION:`;
    personalizationContext.specificFrustrations.forEach((frustration, index) => {
      enhancedPrompt += `\n${index + 1}. "${frustration}" - CREATE ORIGINAL compound insults combining this specific issue with creative profanity. Mine this for personalized metaphors. Build sustained comedic rants (6-8 sentences) targeting THIS exact situation before therapeutic pivot.`;
    });
  }
  
  if (personalizationContext.workStressors.length > 0) {
    enhancedPrompt += `\n\nWORKPLACE COMEDY MINING - TARGET THEIR ACTUAL JOB:`;
    personalizationContext.workStressors.forEach((stressor, index) => {
      enhancedPrompt += `\n${index + 1}. "${stressor}" - Generate workplace-specific roasts using THEIR actual job situation. Create compound insults like "[their boss's name/type] has the leadership skills of a drunk penguin mixed with their specific workplace dysfunction." Build original metaphors from their real work environment.`;
    });
  }

  // Extract specific biographical elements for comedy targeting
  if (variables.work?.company?.length > 0) {
    enhancedPrompt += `\n\nWORKPLACE TARGETING - USE THEIR ACTUAL COMPANY:`;
    variables.work.company.forEach((company, index) => {
      enhancedPrompt += `\n${index + 1}. Company: "${company}" - Create company-specific roasts and metaphors. Use their actual workplace for personalized comedy.`;
    });
  }

  if (variables.relationships?.conflicts?.length > 0) {
    enhancedPrompt += `\nDIFFICULT PEOPLE DISMISSALS - TARGET THEIR ACTUAL RELATIONSHIP CONFLICTS:`;
    variables.relationships.conflicts.forEach((conflict, index) => {
      enhancedPrompt += `\n${index + 1}. "${conflict}" - Create personalized dismissals for this specific relationship conflict. Generate compound insults targeting their particular brand of bullshit. Build sustained roasts about their specific problematic behavior patterns.`;
    });
  }

  if (variables.locations?.home?.length > 0) {
    enhancedPrompt += `\n\nLIVING SITUATION COMEDY - USE THEIR ACTUAL ENVIRONMENT:`;
    variables.locations.home.forEach((location, index) => {
      enhancedPrompt += `\n${index + 1}. "${location}" - Create location-specific metaphors and roasts. Use their actual living situation for grounding and comedy.`;
    });
  }

  if (variables.relationships?.romantic?.length > 0) {
    enhancedPrompt += `\n\nRELATIONSHIP DRAMA MINING - TARGET THEIR ACTUAL ROMANTIC SITUATION:`;
    variables.relationships.romantic.forEach((situation, index) => {
      enhancedPrompt += `\n${index + 1}. "${situation}" - Generate relationship-specific therapeutic roasts. Create personalized dismissals for their actual romantic drama.`;
    });
  }

  if (variables.hobbies?.length > 0) {
    enhancedPrompt += `\n\nHOBBY-BASED METAPHORS - USE THEIR ACTUAL INTERESTS:`;
    variables.hobbies.forEach((hobby, index) => {
      enhancedPrompt += `\n${index + 1}. "${hobby}" - Create hobby-specific metaphors and strength references. Use their actual interests for personalized empowerment.`;
    });
  }
  
  if (personalizationContext.personalStrengths.length > 0) {
    enhancedPrompt += `\n\nPERSONAL STRENGTHS FOR BIOGRAPHICAL EMPOWERMENT:`;
    personalizationContext.personalStrengths.forEach((strength, index) => {
      enhancedPrompt += `\n${index + 1}. "${strength}" - Build empowering language using their actual strengths and victories. Create original strength-based metaphors.`;
    });
  }
  
  if (personalizationContext.environmentalAnchors.length > 0) {
    enhancedPrompt += `\n\nENVIRONMENTAL COMEDY AND GROUNDING - USE THEIR ACTUAL SPACES:`;
    personalizationContext.environmentalAnchors.forEach((anchor, index) => {
      enhancedPrompt += `\n${index + 1}. "${anchor}" - Use this actual place for grounding AND comedy. Create location-specific metaphors and peaceful references.`;
    });
  }
  
  // Add family/relationship references for targeted comedy
  if (variables.names?.family?.length > 0) {
    enhancedPrompt += `\n\nFAMILY DYNAMICS - USE THEIR ACTUAL RELATIONSHIPS:`;
    variables.names.family.forEach((familyMember, index) => {
      enhancedPrompt += `\n${index + 1}. "${familyMember}" - Reference their actual family relationships for support AND comedy. Create family-specific metaphors and dynamics.`;
    });
  }
  
  // Dynamic content generation instructions
  enhancedPrompt += `\n\nDYNAMIC CONTENT GENERATION MANDATES:`;
  enhancedPrompt += `\n- RAW LANGUAGE INTENSITY: ${personalizationContext.rawLanguageIntensity.toUpperCase()}`;
  enhancedPrompt += `\n- COMPOUND INSULT GENERATION: Combine their specific life details with creative profanity`;
  enhancedPrompt += `\n- SITUATIONAL COMEDY: Mine their biography for personalized metaphors and roasts`;
  enhancedPrompt += `\n- SUSTAINED RANTS: Build 6-8 sentence comedic escalations using THEIR specific problems`;
  enhancedPrompt += `\n- BIOGRAPHICAL INTEGRATION: Every creative element must be derived from their actual life`;
  enhancedPrompt += `\n- ANTI-TEMPLATE MANDATE: Generate fresh expressions every session - no recycled jokes`;
  enhancedPrompt += `\n- THERAPEUTIC PURPOSE: All profanity serves their healing and empowerment`;
  enhancedPrompt += `\n- PERSONALIZATION DEPTH: Use their actual people, places, work, relationships for all creative content`;
  
  return enhancedPrompt;
};

export const validateOriginalityInContent = (generatedContent: string): {
  isOriginal: boolean;
  templateMatches: string[];
  suggestions: string[];
} => {
  // Expanded template detection - common repetitive patterns
  const templatePhrases = [
  // BANNED REPETITIVE PATTERNS - Use biographical details instead
  'clusterfuck', 'bullshit buffet', 'fuck that noise', 'eat a bag of dicks',
  'take a long walk off a short pier', 'Satan\'s intern', 'Karen', 'Chad',
  'dickweasel', 'ass hat', 'artisanal horseshit',
  'discount fog machine', 'expired coupon',
  'unpaid intern', 'soggy biscuit', 'drunk penguin', 'caffeinated hamster',
  'judging cat', 'disappointed cat who caught you masturbating', 
  'WiFi connection having a breakdown', 'glittery ass hat', 'premium grade-A organic',
    
    // TEMPLATE SENTENCE STARTERS
    'This is absolutely fucking ridiculous',
    'Tell that bullshit to take a permanent vacation',
    'Your boss is being a total asshole',
    'That meeting sounds like a complete clusterfuck',
    'This situation is genuinely shitty',
    'Family drama can go straight to hell',
    'Tell that anxiety to fuck right off',
    'Fuck that noise, you deserve better',
    
    // GENERIC WORK COMPLAINTS (SHOULD BE PERSONALIZED)
    'emails', 'meetings', 'notifications', 'deadlines',
    'conference calls', 'spreadsheets', 'reports'
  ];
  
  const foundRepeats = templatePhrases.filter(phrase => 
    generatedContent.toLowerCase().includes(phrase.toLowerCase())
  );
  
  // Stricter originality scoring
  const templateScore = Math.max(0, 100 - (foundRepeats.length * 12));
  
  // Check for biographical personalization
  const hasPersonalization = checkBiographicalPersonalization(generatedContent);
  const personalizationScore = hasPersonalization ? 100 : 40;
  
  // Combined originality score
  const finalScore = Math.round((templateScore + personalizationScore) / 2);
  
  const suggestions = finalScore < 80 ? [
    'CRITICAL: Replace generic template phrases with user-specific content',
    'Mine their biography for personalized metaphors and roasts',
    'Create compound insults using their actual life details',
    'Use their real toxic people, not generic "Karen" examples',
    'Build comedy from their specific work situation, not generic "emails"',
    'Generate situational comedy based on their actual environment',
    'Create sustained rants targeting their specific frustrations',
    'Use their actual relationships and living situation for metaphors'
  ] : [
    'Excellent biographical personalization and originality!'
  ];
  
  return {
    isOriginal: finalScore >= 80,
    templateMatches: foundRepeats,
    suggestions
  };
};

// Helper function to detect biographical personalization
const checkBiographicalPersonalization = (content: string): boolean => {
  // Look for signs of personalized content vs generic templates
  const personalizationIndicators = [
    // Personal references
    /your [a-z]+ (boss|colleague|partner|friend|family)/i,
    /at [A-Z][a-z]+ (Company|Corp|Inc|LLC)/,
    /in [A-Z][a-z]+ (city|town|neighborhood)/,
    /with [A-Z][a-z]+ (your|their)/,
    
    // Specific situations vs generic ones
    /dealing with [A-Z][a-z]+('s| is)/,
    /living in [a-z]+ with/i,
    /working at [A-Z]/,
    /relationship with [A-Z]/,
    
    // Personal details vs generic complaints
    /your specific/i,
    /your actual/i,
    /that [A-Z][a-z]+ situation/,
    /[A-Z][a-z]+'s bullshit/
  ];
  
  return personalizationIndicators.some(pattern => pattern.test(content));
};